import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  getBulkAssignmentSummary,
  searchAssignableUsers,
  bulkAssignTasks,
  bulkAssignExternalUsers,
  bulkUpdateTasks
} from 'services/taskService'
import { listContractors } from 'services/contractorService'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { hideEmail } from 'utils/user'
import { SkeletonLine } from 'components/Skeleton'

const KINDS = {
  assignee: {
    title: 'Who is doing this work',
    Icon: GroupOutlinedIcon,
    summaryKey: 'assignees',
    currentHeading: 'On these tasks now',
    emptyCurrent: 'Nobody is assigned to any of the tasks you picked.',
    searchPlaceholder: 'Search people by name',
    addLabel: 'Add to these tasks',
    replaceLabel: 'Replace everyone with this list',
    addHint: 'Keeps whoever is already assigned and adds the people you ticked.',
    replaceHint: 'Removes anyone not ticked, so every task ends up with exactly this list.'
  },
  approver: {
    title: 'Who reviews and signs this work off',
    Icon: FactCheckOutlinedIcon,
    summaryKey: 'approvers',
    currentHeading: 'Approving these tasks now',
    emptyCurrent: 'No approver is set on any of the tasks you picked.',
    searchPlaceholder: 'Search people by name',
    addLabel: 'Add as approvers',
    replaceLabel: 'Replace approvers with this list',
    addHint: 'Keeps the current approvers and adds the people you ticked.',
    replaceHint: 'Removes anyone not ticked, so every task ends up with exactly this list.'
  },
  external: {
    title: 'People outside your organisation',
    Icon: MailOutlineRoundedIcon,
    summaryKey: 'externalAssignees',
    currentHeading: 'Outside people on these tasks now',
    emptyCurrent: 'No one outside your organisation has been given access to these tasks.',
    searchPlaceholder: 'Type an email address',
    addLabel: 'Give these people access',
    replaceLabel: 'Replace outside people with this list',
    addHint: 'Keeps anyone who already has access and adds the people below. Each new person is emailed a link to the task.',
    replaceHint: 'Takes access away from anyone not listed below, and emails a link to everyone new.'
  },
  contractor: {
    title: 'Which contractor is carrying out the work',
    Icon: EngineeringOutlinedIcon,
    summaryKey: 'contractors',
    currentHeading: 'On these tasks now',
    emptyCurrent: 'No contractor is set on any of the tasks you picked.',
    searchPlaceholder: 'Search contractors by name or trade',
    addLabel: null,
    replaceLabel: 'Set contractor on every task',
    addHint: null,
    replaceHint: 'A task can only have one contractor, so this replaces whoever is on it now.'
  }
}

const personName = person =>
  `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
  person.name ||
  (person.email ? hideEmail(person.email) : 'Unknown')

const MAX_BULK_TASKS = 100

const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const BulkAssignPanel = ({ kind, taskIds, onClose, onDone }) => {
  const config = KINDS[kind]

  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState('')

  const [search, setSearch] = useState('')
  const [pool, setPool] = useState([])
  const [poolLoading, setPoolLoading] = useState(false)

  const [selectedIds, setSelectedIds] = useState([])
  const [pinned, setPinned] = useState([])
  const [externalPeople, setExternalPeople] = useState([])
  const [emailInput, setEmailInput] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [emailError, setEmailError] = useState('')

  const [contractorId, setContractorId] = useState('')
  const [contractors, setContractors] = useState([])

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [setObligationDefault, setSetObligationDefault] = useState(false)

  const loadSummary = useCallback(() => {
    if (taskIds.length > MAX_BULK_TASKS) {
      setSummaryLoading(false)
      setSummaryError(
        `You have picked ${taskIds.length} tasks. Bulk changes work on up to ${MAX_BULK_TASKS} at a time: ` +
        'please deselect some and try again.'
      )
      return
    }
    setSummaryLoading(true)
    setSummaryError('')
    getBulkAssignmentSummary(taskIds)
      .then(response => {
        const data = response?.data?.data
        setSummary(data)
        if (kind === 'assignee' || kind === 'approver') {
          setSelectedIds((data?.[config.summaryKey] || []).map(person => person.id))
        }
        if (kind === 'external') {
          setExternalPeople((data?.externalAssignees || []).map(person => ({
            email: person.email,
            name: person.name || ''
          })))
        }
      })
      .catch(() => setSummaryError('We could not check who is on these tasks. Please try again.'))
      .finally(() => setSummaryLoading(false))
  }, [taskIds, kind, config.summaryKey])

  useEffect(() => { loadSummary() }, [loadSummary])

  useEffect(() => {
    if (kind !== 'contractor') return
    listContractors()
      .then(response => setContractors(response?.data?.data?.contractors || []))
      .catch(() => setContractors([]))
  }, [kind])

  useEffect(() => {
    if (kind !== 'assignee' && kind !== 'approver') return
    let cancelled = false
    setPoolLoading(true)
    const timer = setTimeout(() => {
      searchAssignableUsers({ search: search.trim() })
        .then(response => { if (!cancelled) setPool(response?.data?.data || []) })
        .catch(() => { if (!cancelled) setPool([]) })
        .finally(() => { if (!cancelled) setPoolLoading(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [kind, search])

  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  const currentRows = useMemo(
    () => summary?.[config.summaryKey] || [],
    [summary, config.summaryKey]
  )
  const taskCount = summary?.taskCount ?? taskIds.length
  const canActCount = kind === 'contractor'
    ? (summary?.canUpdateCount ?? 0)
    : (summary?.canAssignCount ?? 0)

  const peopleList = useMemo(() => {
    if (kind !== 'assignee' && kind !== 'approver') return []
    const seen = new Set()
    const merged = []
    for (const person of currentRows) {
      seen.add(person.id)
      merged.push(person)
    }
    for (const person of pinned) {
      if (!seen.has(person.id)) { seen.add(person.id); merged.push(person) }
    }
    for (const person of pool) {
      if (!seen.has(person.id)) merged.push(person)
    }
    const term = search.trim().toLowerCase()
    if (!term) return merged
    const pinnedIds = new Set(pinned.map(u => u.id))
    return merged.filter(person => pinnedIds.has(person.id) ||
      [personName(person), person.email].filter(Boolean)
        .some(value => String(value).toLowerCase().includes(term))
    )
  }, [kind, currentRows, pinned, pool, search])

  const filteredContractors = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return contractors
    return contractors.filter(contractor =>
      `${contractor.company || ''} ${contractor.name || ''} ${contractor.trade || ''}`
        .toLowerCase().includes(term)
    )
  }, [contractors, search])

  const togglePerson = id => {
    setSelectedIds(previous =>
      previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]
    )
  }

  const currentUser = useSelector(getCurrentUser)
  const meSelected = currentUser?.id ? selectedIds.includes(currentUser.id) : true
  const addMe = () => {
    if (!currentUser?.id) return
    if (!currentRows.some(u => u.id === currentUser.id)) {
      setPinned(previous => (previous.some(u => u.id === currentUser.id) ? previous : [...previous, currentUser]))
    }
    setSelectedIds(previous => previous.includes(currentUser.id) ? previous : [...previous, currentUser.id])
  }

  const addExternalPerson = () => {
    const email = emailInput.trim().toLowerCase()
    if (!isValidEmail(email)) {
      setEmailError('Enter a full email address, like name@company.co.uk')
      return
    }
    if (externalPeople.some(person => person.email === email)) {
      setEmailError('That person is already on the list.')
      return
    }
    setExternalPeople(previous => [...previous, { email, name: nameInput.trim() }])
    setEmailInput('')
    setNameInput('')
    setEmailError('')
  }

  const removeExternalPerson = email => {
    setExternalPeople(previous => previous.filter(person => person.email !== email))
  }

  const run = async mode => {
    setSaving(true)
    setSaveError('')
    try {
      let response
      if (kind === 'assignee' || kind === 'approver') {
        response = await bulkAssignTasks({
          taskIds,
          userIds: selectedIds,
          role: kind,
          mode,
          setObligationDefault
        })
      } else if (kind === 'external') {
        response = await bulkAssignExternalUsers({
          taskIds,
          people: externalPeople.map(person => ({ email: person.email, name: person.name || null })),
          mode
        })
      } else {
        response = await bulkUpdateTasks({ taskIds, contractorId: contractorId || null })
      }

      const updated = response?.data?.updatedCount ?? response?.data?.updated_count
      if (updated === 0) {
        toast.warning('Nothing changed. You do not have permission to change any of the tasks you picked.')
      } else if (updated !== undefined && updated < taskIds.length) {
        toast.warning(`Updated ${updated} of ${taskIds.length} tasks. You do not have permission to change the rest.`)
      } else {
        toast.success('Tasks updated.')
      }
      onDone?.()
    } catch (error) {
      setSaveError(
        error?.response?.data?.message ||
        'We could not save that. Please check your connection and try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  const HeaderIcon = config.Icon
  const overLimit = taskIds.length > MAX_BULK_TASKS
  const addDisabled = saving || overLimit || canActCount === 0 ||
    (kind === 'external' ? externalPeople.length === 0 : selectedIds.length === 0)
  const replaceDisabled = saving || overLimit || canActCount === 0

  return createPortal(
    <Overlay onClick={onClose} role='presentation'>
      <Dialog onClick={event => event.stopPropagation()} role='dialog' aria-modal='true' aria-label={config.title}>
        <Header>
          <HeaderText>
            <HeaderIconWrap><HeaderIcon /></HeaderIconWrap>
            <div>
              <DialogTitle>{config.title}</DialogTitle>
              <DialogSubtitle>
                {taskCount} task{taskCount === 1 ? '' : 's'} selected
                {canActCount < taskCount && ` · you can change ${canActCount} of them`}
              </DialogSubtitle>
            </div>
          </HeaderText>
          <CloseButton type='button' onClick={onClose} aria-label='Close without changing anything'>
            <CloseIcon />
          </CloseButton>
        </Header>

        <Body>
          <Section>
            <SectionHeading>{config.currentHeading}</SectionHeading>
            {summaryLoading ? (
              <SkeletonStack>
                <SkeletonLine $w='60%' $h={14} />
                <SkeletonLine $w='40%' $h={14} />
              </SkeletonStack>
            ) : summaryError ? (
              <Banner role='alert'>
                <ErrorOutlineIcon style={{ fontSize: 18 }} />
                <span>{summaryError}</span>
                <RetryButton type='button' onClick={loadSummary}>Try again</RetryButton>
              </Banner>
            ) : currentRows.length === 0 ? (
              <HelperText>{config.emptyCurrent}</HelperText>
            ) : (
              <CurrentList>
                {currentRows.map(row => (
                  <CurrentRow key={row.id || row.email}>
                    <CurrentName>
                      {kind === 'contractor'
                        ? (row.company || row.name || 'Unnamed contractor')
                        : personName(row)}
                    </CurrentName>
                    <CurrentCount $all={row.taskCount === taskCount}>
                      on {row.taskCount} of {taskCount}
                    </CurrentCount>
                  </CurrentRow>
                ))}
              </CurrentList>
            )}
          </Section>

          <Section>
            <SectionHeading>
              {kind === 'external' ? 'Add people by email' : 'Choose people'}
            </SectionHeading>

            {kind === 'external' ? (
              <>
                <ExternalInputs>
                  <TextInput
                    value={emailInput}
                    onChange={event => { setEmailInput(event.target.value); setEmailError('') }}
                    placeholder='name@company.co.uk'
                    aria-label='Email address'
                    onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addExternalPerson() } }}
                  />
                  <TextInput
                    value={nameInput}
                    onChange={event => setNameInput(event.target.value)}
                    placeholder='Their name (optional)'
                    aria-label='Their name'
                    onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addExternalPerson() } }}
                  />
                  <AddPersonButton type='button' onClick={addExternalPerson}>Add to list</AddPersonButton>
                </ExternalInputs>
                {emailError && <FieldError>{emailError}</FieldError>}
                {externalPeople.length === 0 ? (
                  <HelperText>Nobody on the list yet. Add an email address above.</HelperText>
                ) : (
                  <ChipRow>
                    {externalPeople.map(person => (
                      <Chip key={person.email}>
                        <span>{person.name ? `${person.name} · ` : ''}{hideEmail(person.email)}</span>
                        <ChipRemove
                          type='button'
                          onClick={() => removeExternalPerson(person.email)}
                          aria-label={`Remove ${person.name || person.email}`}
                        >
                          <CloseIcon style={{ fontSize: 15 }} />
                        </ChipRemove>
                      </Chip>
                    ))}
                  </ChipRow>
                )}
              </>
            ) : (
              <>
                <SearchRow>
                  <SearchOutlinedIcon style={{ fontSize: 19, color: '#8a8a94' }} />
                  <SearchInput
                    data-tour-id='tasks.bulk-assign-search'
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={config.searchPlaceholder}
                    aria-label={config.searchPlaceholder}
                  />
                </SearchRow>

                {(kind === 'assignee' || kind === 'approver') && !meSelected && (
                  <AddMeBtn type='button' onClick={addMe}>
                    + Add me{kind === 'approver' ? ' as approver' : ' as assignee'}
                  </AddMeBtn>
                )}

                {kind === 'contractor' ? (
                  <PickList>
                    <PickRow onClick={() => setContractorId('')} $active={contractorId === ''}>
                      <PickRadio type='radio' checked={contractorId === ''} onChange={() => setContractorId('')} />
                      <PickText>No contractor</PickText>
                    </PickRow>
                    {filteredContractors.length === 0 && (
                      <HelperText>No contractors match that search.</HelperText>
                    )}
                    {filteredContractors.map(contractor => (
                      <PickRow
                        key={contractor.id}
                        onClick={() => setContractorId(contractor.id)}
                        $active={contractorId === contractor.id}
                      >
                        <PickRadio
                          type='radio'
                          checked={contractorId === contractor.id}
                          onChange={() => setContractorId(contractor.id)}
                        />
                        <PickText>
                          {contractor.company || contractor.name || 'Unnamed'}
                          {contractor.trade && <PickMeta> · {contractor.trade}</PickMeta>}
                        </PickText>
                      </PickRow>
                    ))}
                  </PickList>
                ) : poolLoading && peopleList.length === 0 ? (
                  <SkeletonStack>
                    <SkeletonLine $w='55%' $h={14} />
                    <SkeletonLine $w='45%' $h={14} />
                    <SkeletonLine $w='60%' $h={14} />
                  </SkeletonStack>
                ) : peopleList.length === 0 ? (
                  <HelperText>No people match that search.</HelperText>
                ) : (
                  <PickList>
                    {peopleList.map(person => (
                      <PickRow
                        key={person.id}
                        onClick={() => togglePerson(person.id)}
                        $active={selectedIds.includes(person.id)}
                      >
                        <PickCheckbox
                          type='checkbox'
                          checked={selectedIds.includes(person.id)}
                          onChange={() => togglePerson(person.id)}
                          onClick={event => event.stopPropagation()}
                        />
                        <PickText>
                          {personName(person)}
                          {person.taskCount > 0 && (
                            <PickMeta> · already on {person.taskCount} of {taskCount}</PickMeta>
                          )}
                        </PickText>
                      </PickRow>
                    ))}
                  </PickList>
                )}
              </>
            )}
          </Section>

          {(kind === 'assignee' || kind === 'approver') && (
            <ObligationDefaultRow>
              <input
                type='checkbox'
                id='bulkSetObligationDefault'
                checked={setObligationDefault}
                onChange={event => setSetObligationDefault(event.target.checked)}
              />
              <label htmlFor='bulkSetObligationDefault'>
                Also set these people as the default for any compliance obligation in your selection, so its future tasks get the same people. This also updates that obligation’s open tasks.
              </label>
            </ObligationDefaultRow>
          )}

          {saveError && (
            <Banner role='alert'>
              <ErrorOutlineIcon style={{ fontSize: 18 }} />
              <span>{saveError}</span>
            </Banner>
          )}
        </Body>

        <Footer>
          <FooterHints>
            {config.addHint && <FooterHint><strong>Add</strong>: {config.addHint}</FooterHint>}
            <FooterHint><strong>Replace</strong>: {config.replaceHint}</FooterHint>
          </FooterHints>
          <FooterButtons data-tour-id='tasks.bulk-assign-footer'>
            <SecondaryButton type='button' onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
            {config.addLabel && (
              <PrimaryButton type='button' onClick={() => run('add')} disabled={addDisabled}>
                {saving ? 'Saving…' : config.addLabel}
              </PrimaryButton>
            )}
            <DangerButton type='button' onClick={() => run('replace')} disabled={replaceDisabled}>
              {saving ? 'Saving…' : config.replaceLabel}
            </DangerButton>
          </FooterButtons>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body
  )
}

export default BulkAssignPanel

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100300;
  background: rgba(17, 17, 22, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  width: min(720px, 100%);
  max-height: min(88vh, 860px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(17, 17, 22, 0.16);
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #ececf0;
`

const HeaderText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`

const HeaderIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #e6f7f9;
  color: #2aa6b3;
`

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1c1c22;
`

const DialogSubtitle = styled.p`
  margin: 3px 0 0;
  font-size: 14px;
  color: #5f5f6a;
`

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: 1px solid #e2e2e8;
  border-radius: 10px;
  background: #fff;
  color: #4a4a55;
  cursor: pointer;

  &:hover { background: #f1f1f4; }
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SectionHeading = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #2c2c34;
`

const HelperText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #62626d;
`

const FieldError = styled.p`
  margin: 0;
  font-size: 14px;
  color: #b00020;
`

const ObligationDefaultRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e6e6ea;
  border-radius: 10px;
  background: #f6fdfd;

  input { margin-top: 3px; width: 18px; height: 18px; accent-color: #33becc; flex-shrink: 0; cursor: pointer; }
  label { font-size: 14px; line-height: 1.5; color: #3d3d47; cursor: pointer; }
`

const CurrentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid #e6e6ea;
  border-radius: 10px;
  background: #fafafb;
`

const CurrentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 30px;
`

const CurrentName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const CurrentCount = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $all }) => ($all ? '#15803d' : '#7a7a85')};
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 13px;
  min-height: 44px;
  background: #fff;
  border: 1px solid #dedee5;
  border-radius: 10px;
`

const AddMeBtn = styled.button`
  align-self: flex-start;
  margin-top: 10px;
  padding: 8px 13px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  background: #e6f7f9;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.secondary}; color: #fff; }
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  background: transparent;
  color: #1c1c22;
`

const PickList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 280px;
  overflow-y: auto;
`

const PickRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 44px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#e6f7f9' : 'transparent')};

  &:hover { background: ${({ $active }) => ($active ? '#e6f7f9' : '#f1f1f4')}; }
`

const PickCheckbox = styled.input`
  width: 19px;
  height: 19px;
  cursor: pointer;
  accent-color: #33becc;
`

const PickRadio = styled.input`
  width: 19px;
  height: 19px;
  cursor: pointer;
  accent-color: #33becc;
`

const PickText = styled.span`
  font-size: 15px;
  color: #1c1c22;
`

const PickMeta = styled.span`
  font-size: 13px;
  color: #7a7a85;
`

const ExternalInputs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const TextInput = styled.input`
  flex: 1;
  min-width: 180px;
  min-height: 44px;
  padding: 0 13px;
  border: 1px solid #dedee5;
  border-radius: 10px;
  font-size: 15px;
  color: #1c1c22;
  outline: none;

  &:focus { border-color: #33becc; }
`

const AddPersonButton = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid #dedee5;
  border-radius: 10px;
  background: #fff;
  font-size: 15px;
  font-weight: 600;
  color: #3d3d47;
  cursor: pointer;

  &:hover { background: #f1f1f4; }
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px 6px 12px;
  border-radius: 999px;
  background: #e6f7f9;
  color: #1c1c22;
  font-size: 14px;
`

const ChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #4a4a55;
  cursor: pointer;

  &:hover { background: #cceef2; }
`

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fdecea;
  color: #b00020;
  font-size: 14px;
`

const RetryButton = styled.button`
  margin-left: auto;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #b00020;
  border-radius: 8px;
  background: transparent;
  color: #b00020;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 0;
`

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 22px;
  border-top: 1px solid #ececf0;
  background: #fafafb;
`

const FooterHints = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FooterHint = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: #62626d;
`

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`

const SecondaryButton = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid #dedee5;
  border-radius: 8px;
  background: #fff;
  color: #3d3d47;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const PrimaryButton = styled.button`
  min-height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const DangerButton = styled.button`
  min-height: 44px;
  padding: 0 20px;
  border: 1px solid #c2410c;
  border-radius: 8px;
  background: #fff;
  color: #c2410c;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
